import React, { useState } from 'react';
import { Route, Switch, Redirect, useLocation } from 'react-router-dom';

import { Routes } from 'routes';

import { OrderReviewScreen } from 'features/order-summary/screens/OrderReviewScreen';
import { OrderSummaryScreen } from 'features/order-summary/screens/OrderSummaryScreen';
import { OrderPaymentScreen } from 'features/order-summary/screens/OrderPaymentScreen';
import { OrderShippingScreen } from 'features/order-summary/screens/OrderShippingScreen';
import { CartInfoState, initialState } from 'features/order-summary/CartInfoState';
import {
    CartInfoDetails,
    emptyCartInfoDetails,
    useCartDetails,
} from 'features/order-summary/utils/useCartDetails';
import { OrderSuccessScreen } from 'features/order-summary/screens/OrderSuccessScreen';
import { noop } from 'common/utils';
import useIframeHeight from '../components/useIframeHeight';
import { mainWebsiteOrigin } from 'utils';

export enum CheckoutFlowState {
    ENABLED = 'ENABLED',
    DISABLED = 'DISABLED',
}

export interface OrderScreenContextType {
    cartInfo: CartInfoState;
    cartInfoDetails: CartInfoDetails;
    setCartInfo: React.Dispatch<React.SetStateAction<CartInfoState>>;
    checkoutFlowState: CheckoutFlowState;
    setCheckoutFlowState: (_: CheckoutFlowState) => void;
    showFincen: boolean;
    setShowFincen: React.Dispatch<React.SetStateAction<boolean>>;
    updatingLineItem: boolean;
    setUpdatingLineItem: React.Dispatch<React.SetStateAction<boolean>>;
}

export const OrderScreenContext = React.createContext<OrderScreenContextType>({
    cartInfo: initialState,
    cartInfoDetails: emptyCartInfoDetails,
    setCartInfo: () => {
        throw new Error('NotImplemented');
    },
    checkoutFlowState: CheckoutFlowState.DISABLED,
    setCheckoutFlowState: noop,
    showFincen: true,
    setShowFincen: noop,
    updatingLineItem: false,
    setUpdatingLineItem: noop,
});

const CheckoutView = ({ children }: React.PropsWithChildren) => {
    useIframeHeight(mainWebsiteOrigin());
    const { state: locationState } = useLocation<CartInfoState>();
    const [cartInfo, setCartInfo] = useState(locationState || initialState);
    const cartInfoDetails = useCartDetails(cartInfo);
    const [checkoutFlowState, setCheckoutFlowState] = useState<CheckoutFlowState>(
        CheckoutFlowState.DISABLED
    );
    const [showFincen, setShowFincen] = useState<boolean>(true);
    const [updatingLineItem, setUpdatingLineItem] = useState<boolean>(false);

    return (
        <OrderScreenContext.Provider
            value={{
                cartInfo,
                cartInfoDetails,
                setCartInfo,
                checkoutFlowState,
                setCheckoutFlowState,
                showFincen,
                setShowFincen,
                updatingLineItem,
                setUpdatingLineItem,
            }}
        >
            {children}
        </OrderScreenContext.Provider>
    );
};

export const CheckoutScreen = () => {
    return (
        <CheckoutView>
            <Switch>
                <Route path={Routes.ShopOrderScreen} exact>
                    <Redirect to={Routes.ShopOrderSummary} />
                </Route>
                <Route path={Routes.ShopOrderSummary} component={OrderSummaryScreen} />
                <Route path={Routes.ShopOrderPayment} component={OrderPaymentScreen} />
                <Route path={Routes.ShopOrderShipping} component={OrderShippingScreen} />
                <Route path={Routes.ShopOrderReview} component={OrderReviewScreen} />
                <Route path={Routes.ShopOrderSuccess} exact component={OrderSuccessScreen} />
            </Switch>
        </CheckoutView>
    );
};
